import { jsPDF } from 'jspdf';

function texto(valor, fallback = '—') {
  const limpio = String(valor || '').trim();
  return limpio || fallback;
}

function fechaIso(fecha) {
  const raw = String(fecha || '');
  const iso = raw.includes('T') ? raw.split('T')[0] : raw.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : new Date().toISOString().slice(0, 10);
}

function fechaLarga(fecha) {
  const iso = fechaIso(fecha);
  const [y, m, d] = iso.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('es-PE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function tituloTipo(tipo) {
  if (tipo === 'devolucion') return 'CONSTANCIA DE DEVOLUCIÓN DE EPP';
  if (tipo === 'reposicion') return 'CONSTANCIA DE REPOSICIÓN DE EPP';
  return 'CONSTANCIA DE ENTREGA DE EPP';
}

function labelTipoRegistro(tipo) {
  if (tipo === 'devolucion') return 'Devolución';
  if (tipo === 'reposicion') return 'Reposición';
  return 'Entrega';
}

function nombreEmpresa(empresa) {
  return texto(empresa?.nombre_empresa || empresa?.name || empresa?.nombre, 'EMPRESA');
}

function iniciales(nombre) {
  return String(nombre || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase() || '—';
}

function formatoFoto(dataUrl) {
  if (String(dataUrl || '').startsWith('data:image/png')) return 'PNG';
  return 'JPEG';
}

function escribirCampo(doc, etiqueta, valor, x, y, ancho) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(100);
  doc.text(etiqueta, x, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(30);
  const lineas = doc.splitTextToSize(texto(valor), ancho);
  doc.text(lineas, x, y + 4.2);
  return lineas.length > 1 ? 9.5 : 8.2;
}

export async function generarConstanciaEppPdf({
  empresa,
  empleado,
  tallas,
  items,
  fecha,
  tipo = 'entrega',
  numero = null,
  foto = null,
}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const margen = 16;
  const ancho = 210 - margen * 2;
  let y = 16;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(20);
  doc.text(nombreEmpresa(empresa), 105, y, { align: 'center' });
  y += 5.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80);
  if (empresa?.ruc) {
    doc.text(`RUC: ${empresa.ruc}`, 105, y, { align: 'center' });
    y += 4.2;
  }
  const direccion = texto(empresa?.direccion || empresa?.description || empresa?.descripcion, '');
  if (direccion) {
    doc.text(direccion, 105, y, { align: 'center', maxWidth: ancho });
    y += 5;
  } else {
    y += 1.5;
  }

  doc.setDrawColor(20, 120, 110);
  doc.setLineWidth(0.6);
  doc.line(margen, y, 210 - margen, y);
  y += 8;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(20, 120, 110);
  doc.text('DATOS DEL TRABAJADOR', margen, y);
  y += 3.5;

  const fotoW = 28;
  const fotoH = 36;
  const cardH = 46;
  const cardX = margen;
  const cardY = y;
  const dataX = cardX + fotoW + 6;
  const dataAncho = ancho - fotoW - 8;

  doc.setFillColor(248, 251, 250);
  doc.setDrawColor(200, 220, 216);
  doc.setLineWidth(0.3);
  doc.roundedRect(cardX, cardY, ancho, cardH, 1.5, 1.5, 'FD');

  const fotoX = cardX + 3;
  const fotoY = cardY + 3;
  doc.setFillColor(230, 236, 234);
  doc.rect(fotoX, fotoY, fotoW, fotoH, 'F');
  if (foto) {
    try {
      doc.addImage(foto, formatoFoto(foto), fotoX, fotoY, fotoW, fotoH, undefined, 'FAST');
    } catch (_) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(90);
      doc.text(iniciales(empleado?.nombre_completo), fotoX + fotoW / 2, fotoY + fotoH / 2, { align: 'center' });
    }
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(110);
    doc.text(iniciales(empleado?.nombre_completo), fotoX + fotoW / 2, fotoY + 16, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text('Sin foto', fotoX + fotoW / 2, fotoY + 21, { align: 'center' });
  }
  doc.setDrawColor(180);
  doc.rect(fotoX, fotoY, fotoW, fotoH);

  const col1 = dataX;
  const col2 = dataX + dataAncho / 2;
  const colW = dataAncho / 2 - 3;
  let filaY = cardY + 6;
  escribirCampo(doc, 'Nombre completo', empleado?.nombre_completo, col1, filaY, dataAncho - 2);
  filaY += 10;
  escribirCampo(doc, 'Documento', `${texto(empleado?.tipo_documento, 'DNI')} N.º ${texto(empleado?.numero_documento)}`, col1, filaY, colW);
  escribirCampo(doc, 'Cargo', empleado?.cargo, col2, filaY, colW);
  filaY += 10;
  escribirCampo(doc, 'Área', empleado?.areas_de_trabajo, col1, filaY, colW);
  escribirCampo(doc, 'Proyecto', empleado?.proyecto, col2, filaY, colW);
  filaY += 10;
  const tallasTxt = [
    tallas?.talla_pantalon ? `Pantalón ${tallas.talla_pantalon}` : null,
    tallas?.talla_polo ? `Polo ${tallas.talla_polo}` : null,
    tallas?.talla_zapatos ? `Zapatos ${tallas.talla_zapatos}` : null,
  ].filter(Boolean).join('   ');
  escribirCampo(doc, 'Tallas', tallasTxt || 'Sin registrar', col1, filaY, colW);
  escribirCampo(doc, 'Estado', empleado?.estado, col2, filaY, colW);

  y = cardY + cardH + 10;

  doc.setDrawColor(20, 120, 110);
  doc.setLineWidth(0.4);
  doc.line(margen, y, 210 - margen, y);
  y += 8;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(20);
  doc.text(tituloTipo(tipo), 105, y, { align: 'center' });
  y += 5.5;
  if (numero) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text(`${labelTipoRegistro(tipo)} N.º ${numero}`, 105, y, { align: 'center' });
    y += 7;
  } else {
    y += 3;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(40);
  const verbo = tipo === 'devolucion' ? 'devuelto' : 'recibido';
  const intro = `Por medio de la presente se deja constancia que el(la) trabajador(a) identificado(a) en la ficha superior ha ${verbo} los equipos de protección personal (EPP) que se detallan a continuación, en la fecha ${fechaLarga(fecha)}.`;
  const introLineas = doc.splitTextToSize(intro, ancho);
  doc.text(introLineas, margen, y);
  y += introLineas.length * 5 + 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(20);
  doc.text('Detalle de EPP', margen, y);
  y += 4;

  const cols = [
    { x: margen, title: 'N.º' },
    { x: margen + 12, title: 'Descripción' },
    { x: margen + 130, title: 'Cant.' },
    { x: margen + 152, title: 'Estado' },
  ];

  doc.setFillColor(240, 253, 250);
  doc.rect(margen, y, ancho, 7, 'F');
  doc.setFontSize(8);
  doc.setTextColor(40);
  cols.forEach((col) => doc.text(col.title, col.x + 1.5, y + 5));
  y += 7;

  doc.setFont('helvetica', 'normal');
  (items || []).forEach((item, index) => {
    if (y > 250) {
      doc.addPage();
      y = 20;
    }
    const alto = 7;
    doc.setDrawColor(220, 220, 220);
    doc.rect(margen, y, ancho, alto);
    doc.text(String(index + 1), cols[0].x + 1.5, y + 5);
    doc.text(texto(item.nombre, 'EPP'), cols[1].x + 1.5, y + 5);
    doc.text(String(item.cantidad || 1), cols[2].x + 1.5, y + 5);
    doc.text('Bueno', cols[3].x + 1.5, y + 5);
    y += alto;
  });

  y += 8;
  doc.setFontSize(10);
  doc.setTextColor(40);
  const cierre = tipo === 'devolucion'
    ? 'El área SSOMA deja constancia de haber recibido los EPP descritos, verificando su estado al momento de la devolución.'
    : 'El(la) trabajador(a) declara haber recibido los EPP en buen estado, haber sido instruido(a) sobre su uso, cuidado y conservación, y se compromete a utilizarlos durante la jornada, reportar cualquier deterioro y devolverlos al cese o cuando la empresa lo requiera, conforme a la normativa de seguridad y salud en el trabajo.';
  const cierreLineas = doc.splitTextToSize(cierre, ancho);
  doc.text(cierreLineas, margen, y);
  y += cierreLineas.length * 5 + 18;

  if (y > 240) {
    doc.addPage();
    y = 30;
  }

  const firmaY = y + 18;
  doc.setDrawColor(60);
  doc.setLineWidth(0.3);
  doc.line(margen + 8, firmaY, margen + 78, firmaY);
  doc.line(margen + 98, firmaY, margen + 168, firmaY);
  doc.setFontSize(8);
  doc.setTextColor(40);
  doc.text('Trabajador(a)', margen + 43, firmaY + 5, { align: 'center' });
  doc.text(texto(empleado?.nombre_completo), margen + 43, firmaY + 9, { align: 'center', maxWidth: 70 });
  doc.text(`${texto(empleado?.tipo_documento, 'DNI')} ${texto(empleado?.numero_documento)}`, margen + 43, firmaY + 13, { align: 'center' });
  doc.text('Responsable SSOMA', margen + 133, firmaY + 5, { align: 'center' });
  doc.text('Nombre y firma', margen + 133, firmaY + 9, { align: 'center' });

  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(`Emitida el ${fechaLarga(new Date().toISOString().slice(0, 10))}`, 105, 287, { align: 'center' });

  const blob = doc.output('blob');
  const dni = texto(empleado?.numero_documento, 'trabajador').replace(/\s+/g, '');
  const sufijo = numero ? `n${numero}` : fechaIso(fecha);
  return new File([blob], `constancia-epp-${dni}-${sufijo}.pdf`, { type: 'application/pdf' });
}
